import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
  Key as KeyIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import MfaService from '../../services/MfaService';

const MFA_SETUP_STEPS = [
  'Install Authenticator App',
  'Scan QR Code',
  'Verify Setup',
  'Save Backup Codes'
];

interface BackupCode {
  code: string;
  used: boolean;
}

export const MFASetupComponent: React.FC = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [qrCodeData, setQrCodeData] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<BackupCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [codesDownloaded, setCodesDownloaded] = useState(false);

  // Initialize MFA setup
  useEffect(() => {
    if (user && !user.mfa_enabled) {
      initializeMFASetup();
    }
  }, [user]);

  const initializeMFASetup = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call real API to enroll in MFA
      const enrollmentData = await MfaService.enroll();
      
      setSecretKey(enrollmentData.secret);
      // Create QR code data URL from backend SVG
      setQrCodeData(enrollmentData.qr_code_svg);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 2) {
      // Verify the code before proceeding
      handleVerifyCode();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!verificationCode || verificationCode.length !== 6) {
        setError('Please enter a 6-digit verification code');
        return;
      }

      // Call real API to verify enrollment code
      const result = await MfaService.verifyEnrollment({ code: verificationCode });
      
      // Convert backup codes to the expected format
      const codes: BackupCode[] = result.backup_codes.map(code => ({
        code,
        used: false
      }));
      
      setBackupCodes(codes);
      setSuccess('MFA setup verified successfully!');
      setActiveStep(prev => prev + 1);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteMFA = async () => {
    try {
      setLoading(true);
      setError(null);

      // MFA is already enabled after verifyEnrollment, just show success
      setSuccess('MFA has been successfully enabled for your account!');
      setConfirmDialog(false);
      
      // Refresh user data to update mfa_enabled status
      if (user) {
        // Could refresh user data here if AuthContext provides a refresh method
      }
    } catch (err: any) {
      setError(err.message || 'Failed to complete MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
  };

  const downloadBackupCodes = () => {
    const codes = backupCodes.map(({ code }) => code);
    MfaService.downloadBackupCodes(codes, 'vegadigital-mfa-backup-codes.txt');
    setCodesDownloaded(true);
    setSuccess('Backup codes downloaded successfully!');
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Install an Authenticator App
            </Typography>
            <Typography variant="body1" paragraph>
              To use two-factor authentication, you'll need an authenticator app on your mobile device.
              We recommend one of the following apps:
            </Typography>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Box flex={1}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6">Google Authenticator</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Free app available for iOS and Android
                  </Typography>
                </Paper>
              </Box>
              <Box flex={1}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6">Microsoft Authenticator</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Free app with additional features
                  </Typography>
                </Paper>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Once you've installed an authenticator app, click "Next" to continue.
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Scan QR Code
            </Typography>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
              <Box flex={1}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  {qrCodeData ? (
                    <Box>
                      <div 
                        dangerouslySetInnerHTML={{ __html: qrCodeData }}
                        style={{ display: 'flex', justifyContent: 'center', maxWidth: '200px', margin: '0 auto' }}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Scan this QR code with your authenticator app
                      </Typography>
                    </Box>
                  ) : (
                    <CircularProgress />
                  )}
                </Paper>
              </Box>
              <Box flex={1}>
                <Typography variant="body1" paragraph>
                  Open your authenticator app and scan the QR code on the left, or manually enter the secret key below:
                </Typography>
                <TextField
                  fullWidth
                  label="Secret Key"
                  value={secretKey}
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        onClick={() => copyToClipboard(secretKey)}
                        size="small"
                        startIcon={<ContentCopyIcon />}
                      >
                        Copy
                      </Button>
                    )
                  }}
                />
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Keep this secret key secure. You can use it to manually add the account to another authenticator app if needed.
                </Alert>
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Verify Setup
            </Typography>
            <Typography variant="body1" paragraph>
              Enter the 6-digit code from your authenticator app to verify the setup:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              margin="normal"
              inputProps={{ 
                style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' },
                maxLength: 6
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              The code changes every 30 seconds. Enter the current code shown in your authenticator app.
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Save Your Backup Codes
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Save these backup codes in a secure location. 
                You can use them to access your account if you lose your authenticator device.
              </Typography>
            </Alert>
            
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {backupCodes.map((backup, index) => (
                  <Chip
                    key={index}
                    label={backup.code}
                    variant="outlined"
                    size="small"
                    sx={{ fontFamily: 'monospace', minWidth: '120px' }}
                  />
                ))}
              </Box>
            </Paper>

            <Box display="flex" gap={2} mb={2}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={downloadBackupCodes}
                variant="outlined"
              >
                Download Codes
              </Button>
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => copyToClipboard(backupCodes.map(c => c.code).join('\n'))}
                variant="outlined"
              >
                Copy All
              </Button>
            </Box>

            <Alert severity="error">
              <Typography variant="body2">
                Each backup code can only be used once. Generate new codes if you run out.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  if (user?.mfa_enabled) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">
              Two-Factor Authentication Enabled
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Your account is already protected with two-factor authentication.
          </Typography>
          <Button variant="outlined" color="error">
            Disable MFA
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Set Up Two-Factor Authentication
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Two-factor authentication adds an extra layer of security to your account by requiring
        a verification code from your mobile device in addition to your password.
      </Alert>

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {MFA_SETUP_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            
            {activeStep === MFA_SETUP_STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setConfirmDialog(true)}
                disabled={!codesDownloaded}
              >
                Complete Setup
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || (activeStep === 2 && verificationCode.length !== 6)}
              >
                {loading ? 'Verifying...' : 'Next'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Confirm MFA Setup</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to enable two-factor authentication? 
            Make sure you have saved your backup codes in a secure location.
          </Typography>
          <Alert severity="warning">
            Once enabled, you'll need your authenticator app or backup codes to sign in.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCompleteMFA}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Enabling...' : 'Enable MFA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MFASetupComponent;